<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { ROLE_LABEL } from '../api/session'
import { useAffiliate } from '../stores/affiliate'
import { useAiMode } from '../stores/aimode'
import { useComments } from '../stores/comments'
import { useHealth } from '../stores/health'
import { useInbox } from '../stores/inbox'
import { useMenuRound } from '../stores/menuRound'
import { useSession } from '../stores/session'
import { canSeeAds } from '../lib/adsView'
import { canSeeHealth } from '../lib/health'
import { alertDot } from '../lib/menuRound'
import Icon from './ui/Icon.vue'
import ToggleSwitch from './ui/ToggleSwitch.vue'

const store = useInbox()
const session = useSession()
const aimode = useAiMode() // "เสียงเตือน" when the AI hands a chat to me (lib/chime.ts)
const router = useRouter()

const health = useHealth() // สุขภาพระบบ: owner/head only, red dot while a service is down
const showHealth = computed(() => canSeeHealth(session.me?.role))
// a Meta reviewer (/login, api/app/reviewer.py) has chats + comments + ตั้งค่า › ช่องทางที่เชื่อม only: the server
// answers nothing else for them
const isReviewer = computed(() => session.me?.reviewer === true)
const healthLabel = computed(() =>
  health.downCount > 0 ? `สุขภาพระบบ · บริการล่ม ${health.downCount} รายการ` : 'สุขภาพระบบ',
)

const comments = useComments()
comments.start() // rail badge: comments waiting under posts/ads (idempotent)
const commentsLabel = computed(() => `คอมเมนต์ใต้โพสต์และแอด · ต้องตอบ ${comments.totals.todo} รายการ`)

// "เมนูรอบนี้" needs the owner (expired / POS changed / expiring / none approved): a dot on the knowledge button.
// One light call for the owner only, never blocks; the menu store refreshes it after every change.
const menu = useMenuRound()
watch(
  () => session.me?.role,
  (role) => {
    if (role === 'owner') void menu.ensureAlert()
  },
  { immediate: true },
)
const kbDot = computed(() => alertDot(menu.alert, session.me?.role))
// โฆษณา (/ads) + โพสต์ (/posts): the owner's pages only (lib/adsView canSeeAds; App.vue also sends anyone else away)
const showAds = computed(() => canSeeAds(session.me?.role) && !isReviewer.value)
// พาร์ทเนอร์ (/affiliate): owner + head, never the reviewer, only while the server switch is on (stores/affiliate:
// GET /api/v1/affiliate/status once; 404 = off) · a dot while applications or withdrawal requests wait, red once an
// application is past its 2 business days, while a paid shop order has not reached POS (P3), or once a withdrawal request
// is past its 3 business days or the POS status feed is stale / stuck (P4); the label also counts requests whose
// documents wait for the team's check (ruling R10)
const aff = useAffiliate()
watch(
  () => [session.me?.role, session.me?.reviewer] as const,
  () => void aff.ensure(),
  { immediate: true },
)
const affLabel = computed(() =>
  [
    'พาร์ทเนอร์',
    aff.pending > 0 ? `ใบสมัครรออนุมัติ ${aff.pending} ราย` : '',
    aff.attention > 0 ? `คำสั่งซื้อที่ชำระแล้วยังไม่เข้า POS ${aff.attention} รายการ` : '',
    aff.payoutsOpen > 0 ? `คำขอถอนรอโอน ${aff.payoutsOpen} รายการ` : '',
    aff.payoutsLate > 0 ? `เลยกำหนดโอน ${aff.payoutsLate} รายการ` : '',
    aff.payoutsReview > 0 ? `เอกสารรอตรวจ ${aff.payoutsReview} รายการ` : '',
    aff.feedProblem ? 'สถานะจาก POS ไม่อัปเดต' : '',
  ]
    .filter(Boolean)
    .join(' · '),
)
const kbLabel = computed(() => (kbDot.value === 'none' ? 'คลังความรู้ AI' : 'คลังความรู้ AI · เมนูรอบนี้ต้องตรวจ'))
const replyLabel = computed(() => `แชท · ต้องตอบ ${store.counts.needs_reply} แชท`)
const followLabel = computed(() => `ทักตาม · ถึงเวลาทัก ${store.counts.follow_up} ราย`)
const meName = computed(() => session.me?.name ?? store.me?.name ?? '')
const meInitial = computed(() => store.me?.initial ?? session.me?.initial ?? '·')
const meLabel = computed(() =>
  meName.value ? `${meName.value} สถานะ${store.available ? 'พร้อมรับ' : 'พัก'}` : 'บัญชีของฉัน',
)

// ---- account menu (avatar): name + role, ready/away, sign out
const menuOpen = ref(false)
const menuEl = ref<HTMLElement | null>(null)
const meBtn = ref<HTMLElement | null>(null)

function onDocDown(e: MouseEvent): void {
  const t = e.target as Node
  if (!menuEl.value?.contains(t) && !meBtn.value?.contains(t)) menuOpen.value = false
}
function onKey(e: KeyboardEvent): void {
  if (e.key === 'Escape') {
    menuOpen.value = false
    meBtn.value?.focus()
  }
}
function unlisten(): void {
  document.removeEventListener('mousedown', onDocDown)
  document.removeEventListener('keydown', onKey)
}
watch(menuOpen, (open) => {
  if (open) {
    document.addEventListener('mousedown', onDocDown)
    document.addEventListener('keydown', onKey)
  } else unlisten()
})
onBeforeUnmount(unlisten) // e.g. ออกจากระบบ from the open menu: the watch above no longer runs once unmounting starts

async function logout(): Promise<void> {
  try {
    await session.logout()
  } catch {
    store.flash('ออกจากระบบไม่สำเร็จ ลองอีกครั้ง')
  }
}

// แชท → กำลังคุย (ต้องตอบ + รอลูกค้าตอบ, owner 27 ก.ย. 69) · ทักตาม → ทักตาม
function goTab(t: 'active' | 'follow_up'): void {
  store.setTab(t)
  if (router.currentRoute.value.name !== 'inbox' && router.currentRoute.value.name !== 'conversation') {
    void router.push({ name: 'inbox' })
  }
}

const routeName = computed(() => router.currentRoute.value.name)
const inInbox = computed(() => routeName.value === 'inbox' || routeName.value === 'conversation')

function go(name: 'comments' | 'dash' | 'ads' | 'posts' | 'affiliate' | 'config' | 'knowledge' | 'health'): void {
  if (routeName.value !== name) void router.push({ name })
}
</script>

<template>
  <nav class="rail" aria-label="เมนูหลัก">
    <div class="logo" role="img" aria-label="Cleanfit">CF</div>
    <div class="rail-nav">
      <button
        class="rn"
        :class="{ 'is-on': inInbox && store.tab !== 'follow_up' }"
        :aria-current="inInbox && store.tab !== 'follow_up' ? 'page' : undefined"
        :aria-label="replyLabel"
        title="แชท"
        @click="goTab('active')"
      >
        <Icon name="chat" />
        <span v-if="store.replyAlert !== 'none'" class="rn-dot" :class="{ crit: store.replyAlert === 'crit' }" aria-hidden="true"></span>
      </button>
      <button
        class="rn"
        :class="{ 'is-on': inInbox && store.tab === 'follow_up' }"
        :aria-current="inInbox && store.tab === 'follow_up' ? 'page' : undefined"
        :aria-label="followLabel"
        title="ทักตาม"
        @click="goTab('follow_up')"
      >
        <Icon name="history" />
        <span v-if="store.counts.follow_up > 0" class="rn-dot" aria-hidden="true"></span>
      </button>
      <button
        class="rn"
        :class="{ 'is-on': routeName === 'comments' }"
        :aria-current="routeName === 'comments' ? 'page' : undefined"
        :aria-label="commentsLabel"
        title="คอมเมนต์"
        @click="go('comments')"
      >
        <Icon name="comments" />
        <span v-if="comments.alert !== 'none'" class="rn-dot" :class="{ crit: comments.alert === 'crit' }" aria-hidden="true"></span>
      </button>
      <button
        v-if="!isReviewer"
       
        class="rn"
        :class="{ 'is-on': routeName === 'dash' }"
        :aria-current="routeName === 'dash' ? 'page' : undefined"
        aria-label="แดชบอร์ดยอดขายจากแชท"
        title="แดชบอร์ด"
        @click="go('dash')"
      >
        <Icon name="dashboard" />
      </button>
      <button
        v-if="showAds"
        class="rn rn-ads"
        :class="{ 'is-on': routeName === 'ads' }"
        :aria-current="routeName === 'ads' ? 'page' : undefined"
        aria-label="โฆษณา"
        title="โฆษณา"
        @click="go('ads')"
      >
        <Icon name="megaphone" />
      </button>
      <button
        v-if="showAds"
        class="rn rn-posts"
        :class="{ 'is-on': routeName === 'posts' }"
        :aria-current="routeName === 'posts' ? 'page' : undefined"
        aria-label="โพสต์"
        title="โพสต์"
        @click="go('posts')"
      >
        <Icon name="calendar" />
      </button>
      <button
        v-if="aff.visible"
        class="rn rn-aff"
        :class="{ 'is-on': routeName === 'affiliate' }"
        :aria-current="routeName === 'affiliate' ? 'page' : undefined"
        :aria-label="affLabel"
        title="พาร์ทเนอร์"
        @click="go('affiliate')"
      >
        <Icon name="handshake" />
        <span v-if="aff.alert !== 'none'" class="rn-dot" :class="{ crit: aff.alert === 'crit' }" aria-hidden="true"></span>
      </button>
      <button
        v-if="!isReviewer"
       
        class="rn"
        :class="{ 'is-on': routeName === 'knowledge' }"
        :aria-current="routeName === 'knowledge' ? 'page' : undefined"
        :aria-label="kbLabel"
        title="คลังความรู้ AI"
        @click="go('knowledge')"
      >
        <Icon name="book" />
        <span v-if="kbDot !== 'none'" class="rn-dot" :class="{ crit: kbDot === 'crit' }" aria-hidden="true"></span>
      </button>
    </div>
    <div class="rail-foot">
      <button
        v-if="showHealth && !isReviewer"
        class="rn rn-health"
        :class="{ 'is-on': routeName === 'health' }"
        :aria-current="routeName === 'health' ? 'page' : undefined"
        :aria-label="healthLabel"
        title="สุขภาพระบบ"
        @click="go('health')"
      >
        <Icon name="pulse" />
        <span v-if="health.downCount > 0" class="rn-dot crit" aria-hidden="true"></span>
      </button>
      <button
        class="rn"
        :class="{ 'is-on': routeName === 'config' }"
        :aria-current="routeName === 'config' ? 'page' : undefined"
        aria-label="ตั้งค่า"
        title="ตั้งค่า"
        @click="go('config')"
      >
        <Icon name="settings" />
      </button>
      <button
        ref="meBtn"
        class="me"
        :aria-label="meLabel"
        :title="meLabel"
        aria-haspopup="menu"
        :aria-expanded="menuOpen"
        @click="menuOpen = !menuOpen"
      >
        <span class="me-av">{{ meInitial }}</span>
        <span class="me-dot" :class="{ away: !store.available }"></span>
      </button>
    </div>
    <div v-if="menuOpen" ref="menuEl" class="acct" role="menu" aria-label="บัญชีของฉัน">
      <div class="acct-who">
        <span class="acct-av" aria-hidden="true">{{ meInitial }}</span>
        <span class="acct-txt">
          <span class="acct-name">{{ meName || 'บัญชีของฉัน' }}</span>
          <span v-if="session.me" class="acct-role">{{ ROLE_LABEL[session.me.role] }}</span>
        </span>
      </div>
      <button
        class="acct-row"
        role="menuitemcheckbox"
        :aria-checked="store.available"
        @click="store.setAvailable(!store.available)"
      >
        <span>{{ store.available ? 'พร้อมรับแชท' : 'พักอยู่' }}</span>
        <ToggleSwitch :on="store.available" />
      </button>
      <button
        class="acct-row snd"
        role="menuitemcheckbox"
        :aria-checked="aimode.soundOn"
        @click="aimode.setSound(!aimode.soundOn)"
      >
        <span class="snd-l">
          <Icon :name="aimode.soundOn ? 'volume' : 'volumeOff'" />
          <span class="snd-t"><span>เสียงเตือน</span><span class="snd-h">เมื่อ AI ส่งแชทต่อให้คุณ</span></span>
        </span>
        <ToggleSwitch :on="aimode.soundOn" />
      </button>
      <button class="acct-row out" role="menuitem" :disabled="session.signingOut" @click="logout">
        <span>ออกจากระบบ</span>
        <Icon name="logout" />
      </button>
    </div>
  </nav>
</template>

<style scoped>
.rail {
  width: 64px;
  flex: none;
  background: var(--rail);
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 16px 0;
  gap: 24px;
}
.logo {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: #ddf2e7;
  color: var(--rail);
  font: 700 15px/40px var(--mono);
  text-align: center;
  letter-spacing: -0.5px;
}
.rail-nav {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}
.rn {
  position: relative;
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #b9d6c9;
}
.rn:hover {
  background: #15493a;
  color: #fff;
}
.rn.is-on {
  background: #1a5a46;
  color: #fff;
}
.rn :deep(.ic) {
  width: 22px;
  height: 22px;
}
.rn-dot {
  position: absolute;
  top: 9px;
  right: 9px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #8fe3be;
  box-shadow: 0 0 0 2px var(--rail);
}
.rn-dot.crit {
  background: #ff7a6b;
}
.rn.is-on .rn-dot {
  box-shadow: 0 0 0 2px #1a5a46;
}
.rail-foot {
  margin-top: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}
.me {
  position: relative;
  width: 38px;
  height: 38px;
  border-radius: 50%;
}
.me-av {
  display: block;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: #ddf2e7;
  color: var(--rail);
  font-size: 15px;
  font-weight: 600;
  line-height: 38px;
  text-align: center;
}
.me-dot {
  position: absolute;
  right: -1px;
  bottom: -1px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #2bc48a;
  box-shadow: 0 0 0 2.5px var(--rail);
}
.me-dot.away {
  background: #9aa8a1;
}
.rail {
  position: relative;
  /* above the see-through click-away layers of open dropdowns (z 20–21, e.g. ตัวกรองช่องทาง · ย้ายแชท): the
     first click on the rail must navigate, not only close the dropdown (owner: "ชอบกด menu bar ซ้ายมือไม่ได้");
     still below dialogs (30+), the health banner (55), alerts (60) and the image viewer (100) */
  z-index: 25;
}
.acct {
  position: absolute;
  left: 72px;
  bottom: 12px;
  z-index: 60;
  width: 232px;
  padding: 8px;
  background: var(--sf);
  border: 1px solid var(--line);
  border-radius: 12px;
  box-shadow: 0 12px 32px rgba(23, 33, 28, 0.18);
}
.acct-who {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 8px 12px;
  border-bottom: 1px solid var(--line2);
  margin-bottom: 4px;
}
.acct-av {
  flex: none;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: var(--acs);
  color: var(--ac);
  font-size: 15px;
  font-weight: 600;
  line-height: 36px;
  text-align: center;
}
.acct-txt {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.acct-name {
  font-size: 14px;
  font-weight: 600;
  line-height: 1.5;
  color: var(--ink);
  overflow-wrap: anywhere;
}
.acct-role {
  font-size: 12px;
  line-height: 1.5;
  color: var(--mut);
}
.acct-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  min-height: 40px;
  padding: 8px;
  border-radius: 8px;
  font-size: 14px;
  line-height: 1.5;
  color: var(--ink);
}
.acct-row:hover {
  background: var(--bg);
}
.snd-l {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  text-align: left;
}
.snd-l :deep(.ic) {
  flex: none;
  width: 18px;
  height: 18px;
  color: var(--mut);
}
.snd-t {
  display: flex;
  flex-direction: column;
}
.snd-h {
  font-size: 12px;
  line-height: 18px;
  color: var(--mut);
}
.acct-row.out {
  color: var(--crit);
}
.acct-row.out :deep(.ic) {
  width: 18px;
  height: 18px;
}
</style>
